import React from "react";
import { Link } from "react-router-dom";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import { 
  BookOpen, 
  Clock, 
  Star, 
  BookOpenCheck, 
  Trophy,
  CheckCircle2 
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const translations = {
  de: {
    difficultyLabels: {
      beginner: "Anfänger",
      intermediate: "Fortgeschritten",
      advanced: "Experte",
    },
    difficultyVariants: {
      beginner: "success",
      intermediate: "warning",
      advanced: "danger",
    },
    categoryLabels: {
      grundlagen: "Grundlagen",
      bilanzierung: "Bilanzierung",
      kostenrechnung: "Kostenrechnung",
      controlling: "Controlling",
      steuern: "Steuern",
    },
    lesson: "Lektion",
    completed: "Fertig",
    readAgain: "Nochmal lesen",
    start: "Starten",
    quizForLesson: "Quiz zur Lektion"
  },
  en: {
    difficultyLabels: {
      beginner: "Beginner",
      intermediate: "Intermediate",
      advanced: "Advanced",
    },
    difficultyVariants: {
      beginner: "success",
      intermediate: "warning",
      advanced: "danger",
    },
    categoryLabels: {
      grundlagen: "Basics",
      bilanzierung: "Accounting",
      kostenrechnung: "Cost Accounting",
      controlling: "Controlling",
      steuern: "Taxes",
    },
    lesson: "Lesson",
    completed: "Completed",
    readAgain: "Read again",
    start: "Start",
    quizForLesson: "Lesson Quiz"
  }
};

export default function LessonsModuleView({ moduleData, onLessonStart, lessonProgress = [], userId }) {
  const { locale } = useLanguage();
  const t = translations[locale] || translations.de;

  const lessons = moduleData?.lessons
    ?.slice()
    .sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

  const isLessonCompleted = (lessonId) => {
    if (!userId || !lessonProgress) return false;
    return lessonProgress.some(
      (p) => p.lesson_id === lessonId && p.user_id === userId && p.status === "completed"
    );
  };

  return (
    <>
      <div className="mb-4">
        <h2 className="mb-1">{moduleData.title}</h2>
        <div className="text-muted">{moduleData.description}</div>
      </div>

      <Row className="g-4" xs={1} md={2} lg={3}>
        {lessons.map((lesson, idx) => {
          const category = t.categoryLabels[lesson.category] || t.categoryLabels.grundlagen;
          const diffLabel = t.difficultyLabels[lesson.difficulty] || "Level";
          const diffVariant = t.difficultyVariants[lesson.difficulty] || "secondary";
          const isCompleted = isLessonCompleted(lesson.id);

          return (
            <Col md={6} lg={4} key={lesson.id}>
              <Card
                className="h-100 border-0 shadow-sm"
                style={{
                  borderRadius: 18,
                  overflow: "hidden",
                  opacity: isCompleted ? 0.85 : 1,
                  position: "relative",
                  transition: "all 0.2s ease"
                }}
              >
                {/* Completion Badge – perfekter Kreis */}
                {isCompleted && (
                  <div
                    style={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      zIndex: 10,
                      background: "#2ED08A",
                      borderRadius: "50%",
                      width: 36,
                      height: 36,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 2px 8px rgba(46,208,138,0.3)"
                    }}
                  >
                    <CheckCircle2 size={20} color="white" />
                  </div>
                )}

                <Card.Body className="d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="d-flex flex-wrap gap-2">
                      <Badge bg="light" text="dark">
                        {category}
                      </Badge>
                      <Badge bg={diffVariant} text="light">
                        {diffLabel}
                      </Badge>
                    </div>
                    <BookOpen
                      size={20}
                      style={{ color: "#1E88E5", opacity: isCompleted ? 0.5 : 0.7 }}
                    />
                  </div>

                  <Card.Title className="fw-semibold mb-1">
                    {lesson.title}
                  </Card.Title>

                  <div className="text-muted small mb-2">
                    {t.lesson} {lesson.order_index || idx + 1}
                  </div>

                  {lesson.description && (
                    <Card.Text className="text-muted small">
                      {lesson.description}
                    </Card.Text>
                  )}

                  <div className="mt-auto">
                    <Link
                      to={`/module/${encodeURIComponent(moduleData.id)}/lesson/${encodeURIComponent(lesson.id)}`}
                      className="text-decoration-none"
                      onClick={onLessonStart ? () => onLessonStart() : undefined}
                    >
                      <Button
                        className="lesson-start-btn w-100 d-flex align-items-center justify-content-center gap-2"
                        variant={isCompleted ? "outline-primary" : "primary"}
                      >
                        <BookOpenCheck size={18} />
                        {isCompleted ? t.readAgain : t.start}
                      </Button>
                    </Link>

                    <Link
                      to={`/quiz/${encodeURIComponent(moduleData.id)}/${encodeURIComponent(lesson.id)}`}
                    >
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="w-100 mt-2 d-flex align-items-center justify-content-center gap-2"
                      >
                        <Trophy size={16} />
                        {t.quizForLesson}
                      </Button>
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
    </>
  );
}
